import { useApolloClient } from "@apollo/client/react";
import debounce from "p-debounce";
import type { FC } from "react";
import { components } from "react-select";
import Async from "react-select/async";
import { SearchHint, SearchInput } from "src/components/fragments";
import {
  SortDirectionEnum,
  type StudioQuery,
  type StudioQueryVariables,
  StudioSortEnum,
  type StudiosQuery,
  type StudiosQueryVariables,
} from "src/graphql";
import StudioGQL from "src/graphql/queries/Studio.gql";
import StudiosGQL from "src/graphql/queries/Studios.gql";
import { isUUID } from "src/utils";

type Studio = NonNullable<StudioQuery["findStudio"]>;
type StudioParent = { id: string; name: string } | null;
type StudioSlim = Pick<Studio, "id" | "name"> & { parent?: StudioParent };

interface IOptionType {
  value: string;
  label: string;
  sublabel: string | undefined;
  parent: StudioParent;
}

interface StudioSelectProps {
  initialStudio?: StudioSlim | null;
  excludeStudio?: string;
  onChange: (studio: StudioSlim | null) => void;
  onBlur?: React.FocusEventHandler;
  networkSelect?: boolean;
  isClearable?: boolean;
  inputId?: string;
}

const ValueContainer: typeof components.ValueContainer = (props) => (
  <>
    <SearchHint />
    <components.ValueContainer {...props} />
  </>
);

const CLASSNAME = "StudioSelect";
const CLASSNAME_SELECT = `${CLASSNAME}-select`;

const StudioSelect: FC<StudioSelectProps> = ({
  initialStudio,
  excludeStudio,
  onChange,
  onBlur,
  networkSelect = false,
  isClearable = false,
  inputId,
}) => {
  const client = useApolloClient();

  const fetchStudios = async (term: string): Promise<IOptionType[]> => {
    const value = term.trim();
    if (isUUID(value)) {
      if (value === excludeStudio) {
        return [];
      }

      const { data } = await client.query<StudioQuery, StudioQueryVariables>({
        query: StudioGQL,
        variables: { id: value },
      });

      const studio = data?.findStudio;
      if (!studio || (networkSelect && studio.parent !== null)) {
        return [];
      }

      return [
        {
          value: studio.id,
          label: studio.name,
          sublabel: studio.parent?.name,
          parent: studio.parent ?? null,
        },
      ];
    }

    const { data } = await client.query<StudiosQuery, StudiosQueryVariables>({
      query: StudiosGQL,
      variables: {
        input: {
          name: term,
          has_parent: networkSelect ? false : undefined,
          page: 1,
          per_page: 25,
          sort: StudioSortEnum.NAME,
          direction: SortDirectionEnum.ASC,
        },
      },
    });

    if (!data) return [];

    return data?.queryStudios?.studios
      .map((s) => ({
        value: s.id,
        label: s.name,
        sublabel: s.parent?.name,
        parent: s.parent ?? null,
      }))
      .filter((s) => s.value !== excludeStudio);
  };

  const debouncedLoad = debounce(fetchStudios, 200);

  const defaultValue = initialStudio
    ? {
        value: initialStudio.id,
        label: initialStudio.name,
        sublabel: initialStudio.parent?.name,
        parent: initialStudio.parent ?? null,
      }
    : undefined;

  const formatStudioName = (opt: IOptionType) => (
    <>
      <span>{opt.label}</span>
      {opt.sublabel && (
        <small className="bullet-separator parent-studio">{opt.sublabel}</small>
      )}
    </>
  );

  return (
    <div className={CLASSNAME}>
      <Async
        isMulti={false}
        inputId={inputId}
        classNamePrefix="react-select"
        className={`react-select ${CLASSNAME_SELECT}`}
        onChange={(s) =>
          onChange(s ? { id: s.value, name: s.label, parent: s.parent } : null)
        }
        onBlur={onBlur}
        defaultValue={defaultValue}
        loadOptions={debouncedLoad}
        placeholder="Search for studio"
        noOptionsMessage={({ inputValue }) =>
          inputValue === "" ? null : `No studios found for "${inputValue}"`
        }
        isClearable={isClearable}
        formatOptionLabel={formatStudioName}
        components={{
          ValueContainer,
          Input: SearchInput,
        }}
      />
    </div>
  );
};

export default StudioSelect;
